import {
	Grid,
	Typography,
	FormControlLabel,
	Divider,
	IconButton,
	TextField,
	InputAdornment,
	RadioGroup,
	Radio,
	Button,
	Checkbox,
	useTheme,
	Switch,
	Box,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { RiCheckFill } from 'react-icons/ri';
import classNames from 'classnames';
import { getDefaultBranding } from 'common/styles/utils';
import { Branding as BrandingData } from 'common/types';
import React, { useState } from 'react';
import { useTranslation } from 'services/localization';
import { isValidHexColor } from 'common/utils/validation';
import FontSelector from './Fonts/FontSelector';

const MAIN_COLORS = [
	'#f44336',
	'#ff9800',
	'#ffc107',
	'#4caf50',
	'#009688',
	'#2196f3',
	'#7b1fa2',
	'#607d8b',
];

const BUTTON_RADIUS = [
	50, // rounded
	6, // semiRounded
	0, // squared
];

interface Props {
	customBranding?: BrandingData;
	setBranding: (customBranding: BrandingData) => void;
}

const Branding = (props: Props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const customBranding = props.customBranding || null;
	const [disableBranding, setDisableBranding] = useState(!customBranding?.active);
	const [activeColor, setActiveColor] = useState(
		customBranding?.mainColor ?? theme.palette.primary.main,
	);
	const [buttonUppercase, setButtonUppercase] = useState(customBranding?.buttonUppercase ?? true);
	const classes = useStyles(theme);

	const hexColorError =
		!!customBranding?.active && !isValidHexColor(customBranding.mainColor)
			? t(
					'shop.hexColorError',
					'Please input hex color in format #xxx or #xxxxxx, use only characters: #, 0-9, a-f',
			  )
			: null;

	const toggleCustomBranding = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setDisableBranding(!checked);

		props.setBranding({
			...(customBranding || getDefaultBranding()),
			active: checked,
		});
	};

	const handleMainColorClick = (color: string) => {
		setActiveColor(color);
		props.setBranding({
			...(customBranding || getDefaultBranding()),
			mainColor: color,
		});
	};

	const handleHexColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setActiveColor(value);
		props.setBranding({
			...(customBranding || getDefaultBranding()),
			mainColor: value,
		});
	};

	const handleButtonRadiusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;

		props.setBranding({
			...(customBranding || getDefaultBranding()),
			buttonRadius: parseInt(value),
		});
	};

	const toggleButtonUppercase = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setButtonUppercase(checked);
		props.setBranding({
			...(customBranding || getDefaultBranding()),
			buttonUppercase: checked,
		});
	};

	const handleTitleFontChange = (value: string | undefined) => {
		props.setBranding({
			...(customBranding || getDefaultBranding()),
			titleFont: !value
				? null
				: {
						provider: 'google',
						fontFamily: value,
				  },
		});
	};

	const handleBodyFontChange = (value: string | undefined) => {
		props.setBranding({
			...(customBranding || getDefaultBranding()),
			bodyFont: !value
				? null
				: {
						provider: 'google',
						fontFamily: value,
				  },
		});
	};

	return (
		<Box>
			<FormControlLabel
				style={{ alignItems: 'center' }}
				control={
					<Switch
						color="primary"
						checked={!!customBranding ? !!customBranding.active : false}
						onChange={toggleCustomBranding}
						value="customBranding"
					/>
				}
				label={t('shop.enableCustomBranding', 'Enable custom branding')}
			/>
			<Typography
				variant="body2"
				gutterBottom
				className={classes.topSpacing3}
				style={{
					color: !disableBranding ? 'inherit' : theme.palette.action.disabled,
				}}
			>
				{t('shop.mainColor', 'Main color')}
			</Typography>
			<Divider />
			<Grid
				container
				justifyContent="flex-start"
				alignItems="center"
				className={classNames(classes.bottomSpacing)}
			>
				{MAIN_COLORS.map((color) => (
					<Grid item key={color} xs={3} sm="auto">
						<IconButton
							style={{
								backgroundColor: !disableBranding ? color : theme.palette.action.disabledBackground,
							}}
							disabled={!!disableBranding}
							className={classes.mainColorBtn}
							onClick={() => handleMainColorClick(color)}
							size="large"
						>
							{!disableBranding && color === activeColor && <RiCheckFill color="white" size={28} />}
						</IconButton>
					</Grid>
				))}
			</Grid>
			<TextField
				id="hexColor"
				variant="standard"
				label={t('shop.hexColor', 'Hex color')}
				name="hexColor"
				className={classes.textField}
				value={activeColor}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<IconButton
								style={{
									backgroundColor: !disableBranding
										? activeColor
										: theme.palette.action.disabledBackground,
								}}
								disabled={!!disableBranding}
								size="large"
							/>
						</InputAdornment>
					),
				}}
				onChange={handleHexColorChange}
				disabled={!!disableBranding}
				margin="normal"
				type="text"
				error={!!hexColorError}
				helperText={hexColorError}
			/>
			<Typography
				variant="body2"
				gutterBottom
				className={classes.topSpacing3}
				style={{
					color: !disableBranding ? 'inherit' : theme.palette.action.disabled,
				}}
			>
				{t('shop.buttonStyle', 'Button style')}
			</Typography>
			<Divider />
			<RadioGroup
				value={
					customBranding && typeof customBranding.buttonRadius === 'number'
						? customBranding.buttonRadius
						: BUTTON_RADIUS[0]
				}
				onChange={handleButtonRadiusChange}
				className={classes.buttonRadiusSpacing}
				row
			>
				{BUTTON_RADIUS.map((radius, i) => (
					<FormControlLabel
						key={i}
						value={radius}
						control={
							<Radio
								style={{
									color: !disableBranding ? activeColor : undefined,
								}}
								disabled={!!disableBranding}
							/>
						}
						label={
							<Button
								variant="contained"
								color="primary"
								style={{
									backgroundColor: !disableBranding ? activeColor : undefined,
									borderRadius: radius,
									textTransform: buttonUppercase ? 'uppercase' : 'none',
								}}
								disabled={!!disableBranding}
							>
								{t('shop.button', 'Button')}
							</Button>
						}
						labelPlacement="bottom"
					/>
				))}
			</RadioGroup>
			<FormControlLabel
				style={{ alignItems: 'center' }}
				control={
					<Checkbox
						color="primary"
						checked={buttonUppercase}
						disabled={disableBranding}
						onChange={toggleButtonUppercase}
						value="buttonUppercase"
						style={{ color: !disableBranding ? activeColor : undefined }}
					/>
				}
				label={t('shop.buttonUppercase', 'Apply uppercase for buttons')}
			/>
			<Box mt={2}>
				<Typography
					style={{ color: !disableBranding ? 'inherit' : theme.palette.action.disabled }}
					variant="body2"
				>
					{t('account.general.fontSelector.title', 'Typograhy')}
				</Typography>
			</Box>
			<Box my={2}>
				<FontSelector
					disable={disableBranding}
					currentFont={customBranding?.titleFont?.fontFamily}
					label={t('account.general.fontSelector.titleFontLabel', 'Headings & Buttons')}
					onChange={handleTitleFontChange}
				/>
			</Box>
			<Box>
				<FontSelector
					disable={disableBranding}
					currentFont={customBranding?.bodyFont?.fontFamily}
					label={t('account.general.fontSelector.bodyFontLabel', 'Body text')}
					onChange={handleBodyFontChange}
				/>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme) => ({
	bottomSpacing: {
		marginBottom: theme.spacing(2),
	},
	topSpacing3: {
		marginTop: theme.spacing(3),
	},
	textField: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		maxWidth: 380,
		width: `calc(100% - ${theme.spacing(2)})`,
	},
	buttonRadiusSpacing: {
		padding: theme.spacing(1),
	},
	mainColorBtn: {
		height: theme.spacing(5),
		width: theme.spacing(5),
		margin: theme.spacing(2, 1, 0),
		padding: 0,
	},
}));

export default Branding;
